import { Box, Chip } from "@mui/material";
import { type ReactElement } from "react";

import { type LogLevel } from "../types";

interface LogFiltersProps {
  logCounts: Record<LogLevel, number>;
  activeFilters: Set<LogLevel>;
  onToggleFilter: (level: LogLevel) => void;
}

export function LogFilters({
  logCounts,
  activeFilters,
  onToggleFilter,
}: LogFiltersProps): ReactElement {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        padding: "12px 12px",
        background:
          "linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        overflowX: "auto",
        position: "sticky",
        top: 0,
        zIndex: 10,
        flexShrink: 0,
        "&::-webkit-scrollbar": {
          height: 4,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "rgba(255, 255, 255, 0.1)",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(255, 255, 255, 0.3)",
          borderRadius: 2,
        },
      }}
    >
      {logCounts.error > 0 && (
        <Chip
          clickable
          size="small"
          icon={<span style={{ fontSize: "0.8rem" }}>🚨</span>}
          label={`${logCounts.error} error${logCounts.error > 1 ? "s" : ""}`}
          sx={{
            height: 24,
            backgroundColor: "transparent",
            color: "#ff5252",
            fontSize: "0.75rem",
            fontWeight: activeFilters.has("error") ? 600 : 500,
            border: activeFilters.has("error")
              ? "2px solid #ff5252"
              : "1px solid rgba(244, 67, 54, 0.3)",
            borderRadius: 2,
            "& .MuiChip-icon": {
              marginLeft: 1,
              marginRight: 0,
            },
          }}
          onClick={() => {
            onToggleFilter("error");
          }}
        />
      )}
      {logCounts.warn > 0 && (
        <Chip
          clickable
          size="small"
          icon={<span style={{ fontSize: "0.8rem" }}>⚠️</span>}
          label={`${logCounts.warn} warning${logCounts.warn > 1 ? "s" : ""}`}
          sx={{
            height: 24,
            backgroundColor: "transparent",
            color: "#ffab40",
            fontSize: "0.75rem",
            fontWeight: activeFilters.has("warn") ? 600 : 500,
            border: activeFilters.has("warn")
              ? "2px solid #ffab40"
              : "1px solid rgba(255, 152, 0, 0.3)",
            borderRadius: 2,
            "& .MuiChip-icon": {
              marginLeft: 1,
              marginRight: 0,
            },
          }}
          onClick={() => {
            onToggleFilter("warn");
          }}
        />
      )}
      {logCounts.info > 0 && (
        <Chip
          clickable
          size="small"
          icon={<span style={{ fontSize: "0.8rem" }}>ℹ️</span>}
          label={`${logCounts.info} info`}
          sx={{
            height: 24,
            backgroundColor: "transparent",
            color: "#64b5f6",
            fontSize: "0.75rem",
            fontWeight: activeFilters.has("info") ? 600 : 500,
            border: activeFilters.has("info")
              ? "2px solid #64b5f6"
              : "1px solid rgba(33, 150, 243, 0.3)",
            borderRadius: 2,
            "& .MuiChip-icon": {
              marginLeft: 1,
              marginRight: 0,
            },
          }}
          onClick={() => {
            onToggleFilter("info");
          }}
        />
      )}
      {logCounts.log > 0 && (
        <Chip
          clickable
          size="small"
          icon={<span style={{ fontSize: "0.8rem" }}>📝</span>}
          label={`${logCounts.log} log${logCounts.log > 1 ? "s" : ""}`}
          sx={{
            height: 24,
            backgroundColor: "transparent",
            color: "#bdbdbd",
            fontSize: "0.75rem",
            fontWeight: activeFilters.has("log") ? 600 : 500,
            border: activeFilters.has("log")
              ? "2px solid #bdbdbd"
              : "1px solid rgba(158, 158, 158, 0.3)",
            borderRadius: 2,
            "& .MuiChip-icon": {
              marginLeft: 1,
              marginRight: 0,
            },
          }}
          onClick={() => {
            onToggleFilter("log");
          }}
        />
      )}
      {logCounts.debug > 0 && (
        <Chip
          clickable
          size="small"
          icon={<span style={{ fontSize: "0.8rem" }}>🐛</span>}
          label={`${logCounts.debug} debug`}
          sx={{
            height: 24,
            backgroundColor: "transparent",
            color: "#ce93d8",
            fontSize: "0.75rem",
            fontWeight: activeFilters.has("debug") ? 600 : 500,
            border: activeFilters.has("debug")
              ? "2px solid #ce93d8"
              : "1px solid rgba(156, 39, 176, 0.3)",
            borderRadius: 2,
            "& .MuiChip-icon": {
              marginLeft: 1,
              marginRight: 0,
            },
          }}
          onClick={() => {
            onToggleFilter("debug");
          }}
        />
      )}
    </Box>
  );
}
